import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Image from "next/image";
import Link from "next/link";

import { useI18n } from "@/i18n/I18nProvider";
import { colors } from "@/utils/const";

const ROLE_MAX_LENGTH = 35;

const CORNER_VECTOR_SRC =
  "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/board-vector.webp";

const UNDERLINE_SRC =
  "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/bussines-line.webp";

const gridSx = { justifyContent: "center" };

const gridItemSx = {
  display: "flex",
  justifyContent: { xs: "center", md: "space-between" },
};

const cardSx = {
  position: "relative",
  height: { xs: "300px", lg: "320px" },
  width: { xs: "300px", md: "360px", lg: "500px" },
  mb: "30px",
};

const photoFrameSx = {
  width: "100%",
  height: "100%",
  borderRadius: "15px",
  overflow: "hidden",
  position: "relative",
  backgroundColor: colors.bg_grey,
};

const photoSx = { objectFit: "cover", borderRadius: "15px" };

const captionSx = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  width: "250px",
  transform: "translate(-50%, -50%)",
  position: "absolute",
  top: "100%",
  left: "50%",
  zIndex: 10,
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  padding: "10px",
  pb: 0,
  overflow: "hidden",
  textDecoration: "none",
  boxShadow:
    "0px 2px 10px 0px rgba(4, 9, 33, 0.05), 0px 2px 0px 0px rgba(4, 9, 33, 0.05), 0px 0px 5px 0px rgba(4, 9, 33, 0.03)",
};

const nameSx = {
  color: "#2d317f",
  fontSize: "17px",
  fontWeight: "bold",
};

const readMoreWrapperSx = {
  position: "relative",
  display: "inline-block",
  zIndex: 1,
  alignSelf: "flex-end",
  pb: 1,
};

const readMoreSx = { color: "#f18222", fontSize: "12px" };

const underlineSx = {
  position: "absolute",
  bottom: 0,
  left: 0,
  height: "3px",
  width: "130%",
  pointerEvents: "none",
};

function truncate(value, maxLength, suffix = "...") {
  const text = String(value ?? "");

  if (text.length <= maxLength) return text;

  return text.slice(0, maxLength - suffix.length) + suffix;
}

function MemberCard({ member, locale, readMoreLabel }) {
  const isArabic = locale === "ar";

  // Only members with a written biography link through to a profile page.
  const hasProfile = Boolean(member?.details);
  const profileHref = hasProfile ? `/profile-details/${member.slug}` : undefined;

  const name = member?.name?.text?.[locale];
  const role = member?.role?.text?.[locale];

  return (
    <Box sx={cardSx}>
      <Box sx={photoFrameSx}>
        <Image
          src={member.img}
          alt={member.img_alt || name || ""}
          fill
          sizes="(max-width:600px) 300px, (max-width:900px) 360px, 500px"
          quality={100}
          style={photoSx}
        />
      </Box>

      <Box
        component={hasProfile ? Link : "div"}
        href={profileHref}
        sx={{ ...captionSx, cursor: hasProfile ? "pointer" : "default" }}
      >
        <Box
          component="img"
          src={CORNER_VECTOR_SRC}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          sx={{
            position: "absolute",
            top: 0,
            left: isArabic ? 0 : "unset",
            right: isArabic ? "unset" : 0,
            transform: isArabic ? "scaleX(1)" : "scaleX(-1)",
            pointerEvents: "none",
          }}
        />

        <Box sx={nameSx}>{name}</Box>

        <Box
          sx={{
            color: "#686b7a",
            fontSize: "12px",
            fontWeight: "bold",
            pb: hasProfile ? 0 : 2,
          }}
        >
          {hasProfile ? truncate(role, ROLE_MAX_LENGTH) : role}
        </Box>

        {hasProfile && (
          <Box sx={readMoreWrapperSx}>
            <span style={readMoreSx}>{readMoreLabel}</span>
          </Box>
        )}

        <Box
          component="img"
          src={UNDERLINE_SRC}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          sx={underlineSx}
        />
      </Box>
    </Box>
  );
}

/**
 * Grid of board members used by the About page.
 */
export default function NewMemberCard({ boardMembers = [] }) {
  const { t, locale } = useI18n();

  return (
    <Grid container spacing={{ xs: 6, md: 8 }} sx={gridSx}>
      {boardMembers?.map((member, index) => (
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={3}
          key={member?.id ?? index}
          sx={gridItemSx}
        >
          <MemberCard
            member={member}
            locale={locale}
            readMoreLabel={t.Readmore}
          />
        </Grid>
      ))}
    </Grid>
  );
}
